/**
 * Per-org / per-branch consent form configuration. Stored under
 * `organizations.settings.consent_form` and `branches.settings.consent_form`.
 *
 * Resolution order: branch (when `override = true`) → org → DEFAULT_CONSENT_CONFIG.
 * A partial saved config is deep-merged with the defaults at read time so future
 * default updates flow to orgs that haven't customized that specific field.
 */
export interface ConsentFormFields {
  allergies: boolean;
  injuries: boolean;
  other_conditions: boolean;
  pregnancy: boolean;
}

export interface ConsentFormConfig {
  enabled: boolean;
  dialog_title: string;
  intro_text: string;
  acknowledgement_text: string;
  fields: ConsentFormFields;
}

/** Branch-level config carries the override flag in addition to the base shape. */
export interface BranchConsentFormConfig extends ConsentFormConfig {
  override: boolean;
}

export const DEFAULT_CONSENT_CONFIG: ConsentFormConfig = {
  enabled: true,
  dialog_title: 'Health & consent',
  intro_text:
    'Tap anything that applies so your therapist can adjust treatment safely. All details stay confidential.',
  acknowledgement_text:
    'I confirm the information above is accurate to the best of my knowledge. I understand the salon and its therapists are not liable for any adverse reactions caused by conditions I did not disclose. I consent to the treatments I have selected and acknowledge the cancellation policy.',
  fields: {
    allergies: true,
    injuries: true,
    other_conditions: true,
    pregnancy: true,
  },
};

/** Single source of truth for consent field labels. Used by the org settings
 *  page, branch settings override, and the customer-facing booking dialog. */
export const CONSENT_FIELD_LABELS: Record<keyof ConsentFormFields, string> = {
  allergies: 'Allergies or sensitivities',
  injuries: 'Recent injuries or surgery',
  other_conditions: 'Other conditions',
  pregnancy: 'Currently pregnant',
};
